import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { FOR_INVESTORS_SECTIONS, FOR_INVESTORS_SUB_SECTIONS } from 'constants/businessToolSections/forInvestors';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { DepartmentPosition } from 'components/BusinessToolForm/Sections/ManagementAndOrganization/StaffOrganization/DepartmentPosition';
import styles from 'components/BusinessToolForm/Sections/ManagementAndOrganization/StaffOrganization/StaffOrganization.module.scss';
import {
  LEVEL2_DEPARTMENT_NAMES,
  level2DepartmentValues,
  level3DepartmentValues,
  prefix,
  STAFF_ORGANIZATION_NAMES,
} from 'components/BusinessToolForm/Sections/ManagementAndOrganization/StaffOrganization/utils/form';

import { concatWithDot } from 'helpers/string';

export const StaffOrganization: React.FC = () => {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_INVESTORS_SECTIONS.MANAGEMENT_AND_ORGANIZATION,
    FOR_INVESTORS_SUB_SECTIONS.FOR_INVESTORS_MANAGEMENT_AND_ORGANIZATION.STAFF_ORGANIZATION,
  );

  const firstLevelPosition = getValues(concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.LEVEL1_DEPARTMENT));

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('management.title')}
          badgeText={t('management.staff.title')}
          section={FOR_INVESTORS_SECTIONS.MANAGEMENT_AND_ORGANIZATION}
        />
      </FormSectionCard>
      {/* 7.3.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.staff.numberOfStaff')}
        aiFieldName={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.NUMBER_OF_STAFF)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.NUMBER_OF_STAFF)}
          name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.NUMBER_OF_STAFF)}
          minRows={3}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 7.3.2 */}
      <FormSectionCard disabled={!canAccess} title={t('management.staff.organizationalStructure')} collapsible>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormSectionFilledCard title={t('management.staff.firstLevel')} className={styles.sectionContainer}>
              <Grid container>
                <Grid item xs={6}>
                  <TextInputControl
                    label={t('management.staff.departmentPosition')}
                    control={control}
                    id={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.LEVEL1_DEPARTMENT)}
                    name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.LEVEL1_DEPARTMENT)}
                    disabled={!canAccess}
                    maxLength={50}
                  />
                </Grid>
              </Grid>
            </FormSectionFilledCard>
          </Grid>
          <Grid item xs={12}>
            <FormSectionFilledCard
              title={t('management.staff.secondLevel', { firstLevelPosition })}
              className={styles.sectionContainer}
            >
              <FieldArrayComponent
                name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.LEVEL2_DEPARTMENT)}
                initialItem={level2DepartmentValues}
                buttonText={t('management.staff.department')}
                fieldArrayStyles={{
                  removeButton: styles.removeButton,
                  container: styles.container,
                  rowContainer: styles.rowContainer,
                  addButton: styles.addButton,
                }}
                disabled={!canAccess}
              >
                {(namePrefix) => (
                  <TextInputControl
                    control={control}
                    id={concatWithDot(namePrefix, LEVEL2_DEPARTMENT_NAMES.DEPARTMENT)}
                    name={concatWithDot(namePrefix, LEVEL2_DEPARTMENT_NAMES.DEPARTMENT)}
                    label={t('management.staff.departmentPosition')}
                    disabled={!canAccess}
                    maxLength={50}
                  />
                )}
              </FieldArrayComponent>
            </FormSectionFilledCard>
          </Grid>
          <Grid item xs={12}>
            <FormSectionFilledCard title={t('management.staff.thirdLevel')} className={styles.sectionContainer}>
              <FieldArrayComponent
                notRequired
                name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.LEVEL3_DEPARTMENT)}
                initialItem={level3DepartmentValues}
                buttonText={t('management.staff.department')}
                fieldArrayStyles={{ removeButton: styles.removeButton, addButton: styles.addButton }}
                disabled={!canAccess}
                maxLength={10}
              >
                {(namePrefix) => <DepartmentPosition name={namePrefix} disabled={!canAccess} />}
              </FieldArrayComponent>
            </FormSectionFilledCard>
          </Grid>
        </Grid>
      </FormSectionCard>
      {/* 7.3.3 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.staff.skills')}
        aiFieldName={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.SKILLS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.SKILLS)}
          name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.SKILLS)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 7.3.4 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.staff.additionalHires')}
        aiFieldName={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.ADDITIONAL_HIRES)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.ADDITIONAL_HIRES)}
          name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.ADDITIONAL_HIRES)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 7.3.7 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.staff.salaryPolicy')}
        aiFieldName={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.SALARY_POLICY)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.SALARY_POLICY)}
          name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.SALARY_POLICY)}
          minRows={3}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
    </>
  );
};
